import {
  IconButton,
  Toast,
} from '@fattureincloud/fic-design-system'
import { faTrash } from '@fortawesome/pro-light-svg-icons'
import React, { FC, useCallback, useEffect, useState } from 'react'
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd'

import { httpRequest } from '../common/network'
import { getIcon } from '../common/utils'
import {
  DraggableContent,
  SectionsBoxes,
} from '../layout_components/PageSection'
import { PageSectionBlock } from '../layout_components/PageSectionBlock'
import { errorMessage, locale } from '../locales/locale'
import { useAppSelector } from '../redux/hooks'
import Chat from './Chat'
import {
  Confirm,
  MainSectionDroppable,
  Section,
  SectionDescription,
  SectionEditableLoading,
  SectionIcon,
  SectionLoading,
  SectionTitle,
} from './common.components'
import { MainContainer, Separator } from './customComponents'
import { ArticleInfo, SectionInfo } from './definitions'
import EditorModeToggle from './EditorMode/EditorModeToggle'
import { MainPageProps } from './external/customComponents'
import FBCommunityBanner from './Header/FBCommunityBanner'
import HelpHeader from './Header/HelpHeader'
import PrivacyMessage from './PrivacyMessage'
import SectionEditButtons from './Sections/SectionEditButtons'

const MainPage: FC<MainPageProps> = (props: MainPageProps) => {
  const [sections, setSections] = useState<SectionInfo[]>([])
  const [editSections, setEditSections] = useState<SectionInfo[]>([])
  const [selectedSection, setSelectedSection] = useState<SectionInfo>()
  const [deleteSectionConfirm, setDeleteSectionConfirm] =
    useState<boolean>(false)
  const [isLoadingSections, setIsLoadingSections] = useState<boolean>(false)
  const editorModeActive = useAppSelector((state) => state.editor.value)
  const realm = useAppSelector((state) => state.realm.value)
  const visualizationMode = useAppSelector(
    (state) => state.visualizationMode.value
  )
  const adminMode = useAppSelector((state) => state.adminMode.value)

  const loadCategories = useCallback(() => {
    setIsLoadingSections(true)
    httpRequest({
      method: 'GET',
      path: 'help/list',
      params: {
        realm: realm,
        visualize_mode: adminMode ? visualizationMode : 0,
      },
      onSuccess: (response: {
        data: {
          sections: SectionInfo[]
          articles: ArticleInfo[]
        }
      }) => {
        setSections(response.data.sections)
        setEditSections(response.data.sections)
        setIsLoadingSections(false)
      },
      onError: (res) => Toast.error(errorMessage, { content: res.message }),
    })
  }, [editorModeActive, realm, visualizationMode, adminMode])

  useEffect(loadCategories, [loadCategories])

  const saveMainSectionOrder = () => {
    httpRequest({
      method: 'PUT',
      path: 'help/sections/order',
      data: { sections: editSections },
      onSuccess: () => {
        Toast.success(locale('Ordine salvato correttamente'))
        setSections(editSections) // allineamento delle sezioni con l'ordine appena modificato
      },
      onError: (res) => Toast.error(errorMessage, { content: res.message }),
    })
  }

  const renderLoadingBoxes = () => {
    const data = [1, 2, 3, 4, 5, 6]
    return data.map((content, idx) => {
      return <Section key={idx}>{SectionLoading}</Section>
    })
  }

  const renderDroppableLoading = () => {
    const data = [1, 2, 3, 4, 5, 6]
    return data.map((content, idx) => {
      return <Section key={idx}>{SectionEditableLoading}</Section>
    })
  }

  const dragEndHandle = (result: DropResult) => {
    // dropped inside the list
    const { destination, source } = result
    if (source && destination) {
      setEditSections((prevState) => {
        const items = Array.from(prevState)
        const [reorderedItem] = items.splice(result.source.index, 1)
        items.splice(result.destination.index, 0, reorderedItem)
        return [...items]
      })
    }
  }

  const deleteSection = () => {
    httpRequest({
      method: 'DELETE',
      path: `help/sections/${selectedSection.id}`,
      onSuccess: () => {
        Toast.success(locale('Sezione eliminata'))
        loadCategories()
      },
      onError: (res) => Toast.error(errorMessage, { content: res.message }),
    })
  }

  const renderEditCategories = () => {
    return (
      <>
        {deleteSectionConfirm && selectedSection && (
          <Confirm
            title={locale('Eliminare sezione ') + selectedSection.title}
            type={'warning'}
            question={locale('Vuoi eliminare definitivamente la sezione?')}
            description={
              locale("Procedendo con l'operazione") + ', ' +
              locale('tutte le informazioni relative alla sezione ') +
              locale('verranno eliminate definitivamente')
            }
            actionText={locale('Elimina')}
            cancelText={locale('Annulla')}
            onAction={deleteSection}
            isOpen={deleteSectionConfirm}
            setIsOpen={setDeleteSectionConfirm}
          />
        )}
        <SectionEditButtons
          reloadSection={loadCategories}
          parent_section={null}
          saveOrder={saveMainSectionOrder}
        />
        <DragDropContext onDragEnd={dragEndHandle}>
          <SectionsBoxes>
            <Droppable droppableId='mainSections'>
              {(provided) => (
                <DraggableContent
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {editSections?.map((content, idx) => (
                    <Draggable key={idx} draggableId={String(idx)} index={idx}>
                      {(
                        {
                          dragHandleProps: eventHandlers,
                          draggableProps,
                          innerRef,
                        },
                        { isDragging }
                      ) => (
                        <Section
                          ref={innerRef}
                          {...draggableProps}
                          {...eventHandlers}
                          isdragging={isDragging ? 1 : 0}
                          key={idx}
                          onClick={() =>
                            props.changeView(content.permalink, 'sezione')
                          }
                        >
                          <MainSectionDroppable>
                            <div>
                              <SectionIcon icon={getIcon(content.icon)} />
                              <SectionTitle>
                                {content.id} - {content.title}
                              </SectionTitle>
                              <SectionDescription>
                                {content.description}
                              </SectionDescription>
                            </div>
                            <div>
                              <IconButton
                                icon={{ icon: faTrash, color: 'red' }}
                                onClick={(e) => {
                                  setSelectedSection(content)
                                  setDeleteSectionConfirm(true)
                                  e.stopPropagation()
                                }}
                              />
                            </div>
                          </MainSectionDroppable>
                        </Section>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </DraggableContent>
              )}
            </Droppable>
          </SectionsBoxes>
        </DragDropContext>
      </>
    )
  }

  const renderCategories = () => {
    return sections?.map((content, idx) => {
      return (
        <Section
          key={idx}
          onClick={() => props.changeView(content.permalink, 'sezione')}
        >
          <>
            <SectionIcon icon={getIcon(content.icon)} />
            <SectionTitle>
              {editorModeActive && <>{content.id} - </>}
              {content.title}
            </SectionTitle>
            <SectionDescription>{content.description}</SectionDescription>
          </>
        </Section>
      )
    })
  }

  return (
    <MainContainer
      className={props.className ? props.className : undefined}
      isExternal={false}
      isMainPage={true}
    >
      {realm === 'fic' && <FBCommunityBanner />}
      <HelpHeader
        closeIcon={!props.embedded}
        onClickClose={props.onClickClose}
        onSelect={props.changeView}
        embedded={props.embedded}
      />
      <EditorModeToggle realmChoice={true} />
      {!editorModeActive && (
        <SectionsBoxes>
          <PageSectionBlock>
            {isLoadingSections ? renderLoadingBoxes() : renderCategories()}
          </PageSectionBlock>
        </SectionsBoxes>
      )}
      {editorModeActive && !isLoadingSections && renderEditCategories()}
      {editorModeActive && isLoadingSections && (
        <DraggableContent>{renderDroppableLoading()}</DraggableContent>
      )}
      {!editorModeActive && (
        <>
          <Separator />
          <Chat />
          <PrivacyMessage />
        </>
      )}
    </MainContainer>
  )
}

export default MainPage
